// /verify-email?token=… — email magic-link 落地頁（00 §3 / core §3）。
// 信中連結指向本 SPA 落地頁（不含地區前綴）；落地頁以 token 呼叫 POST
// /v3/auth/verify-email（端點只收 POST、消費一次性 token），
// 讓使用者看到友善結果（成功→導登入；410 過期/已用→提示重寄）。

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { ApiError, verifyEmail } from '../../lib/api';

type State = 'verifying' | 'ok' | 'expired' | 'error' | 'no-token';

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const { status } = useAuth();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<State>(token ? 'verifying' : 'no-token');
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true; // verify-email consumes the token (one-shot) — never double-fire (StrictMode).
    verifyEmail(token).then(
      () => setState('ok'),
      (err) => {
        if (err instanceof ApiError && (err.status === 410 || err.code === 'VERIFY_TOKEN_EXPIRED')) setState('expired');
        else setState('error');
      }
    );
  }, [token]);

  if (status === 'authenticated') {
    return <Navigate to="/devices" replace />;
  }

  const body = (() => {
    switch (state) {
      case 'verifying':
        return <p className="text-secondary">{t('auth.verify.verifying')}</p>;
      case 'ok':
        return (
          <>
            <h1 style={{ marginTop: 0, fontSize: 'var(--font-size-3xl)' }}>{t('auth.verify.okTitle')}</h1>
            <p className="text-secondary">{t('auth.verify.okBody')}</p>
            <Link className="btn-primary" to="/login">
              {t('auth.toLogin')}
            </Link>
          </>
        );
      case 'expired':
        return (
          <>
            <h1 style={{ marginTop: 0, fontSize: 'var(--font-size-3xl)' }}>{t('auth.verify.expiredTitle')}</h1>
            <p className="text-secondary">{t('auth.verify.expiredBody')}</p>
            <Link className="btn-primary" to="/login">
              {t('auth.backToLogin')}
            </Link>
          </>
        );
      case 'no-token':
        return (
          <>
            <h1 style={{ marginTop: 0, fontSize: 'var(--font-size-3xl)' }}>{t('auth.verify.noTokenTitle')}</h1>
            <p className="text-secondary">{t('auth.verify.noTokenBody')}</p>
            <Link className="btn-primary" to="/login">
              {t('auth.backToLogin')}
            </Link>
          </>
        );
      default:
        return (
          <>
            <h1 style={{ marginTop: 0, fontSize: 'var(--font-size-3xl)' }}>{t('auth.verify.failTitle')}</h1>
            <p className="text-secondary">{t('auth.verify.failBody')}</p>
            <Link className="btn-primary" to="/login">
              {t('auth.backToLogin')}
            </Link>
          </>
        );
    }
  })();

  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--spacing-8)' }}>
      <section className="card" style={{ width: 'min(400px, 100%)', textAlign: 'center' }}>{body}</section>
    </div>
  );
}
